import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../../../features/auth/authSlice';
import { useAddFavoriteMutation, useDeleteFavoriteMutation } from '../../../features/api/apiSlice';
import Button from '../../../components/ui/Button';

const RecentlyViewed = ({ recentlyViewed }) => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [addFavorite] = useAddFavoriteMutation();
  const [deleteFavorite] = useDeleteFavoriteMutation();

  const scroll = (direction) => {
    if (scrollRef?.current) {
      const scrollAmount = 300;
      scrollRef?.current?.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const viewProperty = (propertyId) => {
    navigate(`/property-detail?id=${propertyId}`);
  };

  const handleFavoriteToggle = async (e, property) => {
    e.stopPropagation(); // Prevent navigating to property detail
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (property?.isFavorite) {
        await deleteFavorite(property?.id);
      } else {
        await addFavorite(property?.id);
      }
      // Optimistically update UI or refetch data if needed
      // For now, assuming the API call will trigger a re-render via RTK Query invalidation
    } catch (error) {
      console.error('Failed to toggle favorite status:', error);
    }
  };

  if (recentlyViewed?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Recently Viewed</h2>
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Eye" size={24} className="text-muted-foreground" />
          </div>
          <p className="text-muted-foreground mb-4">No recently viewed properties</p>
          <Button
            variant="outline"
            onClick={() => navigate('/property-search-browse')}
            iconName="Search"
            iconPosition="left"
          >
            Browse Properties
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Recently Viewed</h2>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            iconName="ChevronLeft"
            onClick={() => scroll('left')}
            disabled={recentlyViewed?.length <= 3}
          />
          <Button
            variant="ghost"
            size="sm"
            iconName="ChevronRight"
            onClick={() => scroll('right')}
            disabled={recentlyViewed?.length <= 3}
          />
        </div>
      </div>
      <div 
        ref={scrollRef}
        className="flex space-x-4 overflow-x-auto scrollbar-hide pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {recentlyViewed?.map((property) => (
          <div
            key={property?.id}
            className="flex-shrink-0 w-64 bg-background rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => viewProperty(property?.id)}
          >
            <div className="relative h-40">
              <Image
                src={property?.images?.[0]}
                alt={property?.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2">
                <button
                  className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                  onClick={(e) => handleFavoriteToggle(e, property)}
                >
                  <Icon
                    name="Heart"
                    size={16}
                    className={property?.isFavorite ? 'text-red-500' : 'text-muted-foreground'}
                  />
                </button>
              </div>
              <div className="absolute bottom-2 left-2">
                <span className="bg-primary text-white px-2 py-1 rounded text-sm font-medium">
                  ${property?.price?.toLocaleString()}
                </span>
              </div>
            </div>
            
            <div className="p-4">
              <h3 className="font-medium text-foreground mb-1 truncate">
                {property?.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-2 truncate">
                {property?.location}
              </p>
              
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Bed" size={14} />
                  <span>{property?.bedrooms}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Bath" size={14} />
                  <span>{property?.bathrooms}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Square" size={14} />
                  <span>{property?.sqft}</span>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground mt-2">
                Viewed {property?.viewedDate}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentlyViewed;
